/**
 * `jdAnchors` — split AI-evaluation markdown into markdown + JD-anchor segments.
 *
 * The AI Evaluation ("show your work") anchors each assessment point to a
 * verbatim job-description requirement. The backend faithfulness guard
 * (``app/services/ai/jd_anchor.py``) emits the surviving, grounded anchors
 * inline in the stored markdown as::
 *
 *     [[JD_ANCHOR: <verbatim requirement copied from the JD>]]
 *
 * This pure function splits a markdown source string into an ordered list of
 * segments so the renderer can draw each anchor as the blue quote-rule
 * (`.jd-anchor` in the v2 mockup) positioned exactly where the model placed it
 * — beneath the point it anchors.
 *
 * Legacy evals (generated before anchors existed) contain no markers → a single
 * markdown segment, no anchors, no error. Graceful by construction.
 */

type EvalSegment =
  | { readonly kind: "markdown"; readonly text: string }
  | { readonly kind: "anchor"; readonly quote: string };

// Matches the backend marker. Non-greedy body; `[\s\S]` so a quote that wrapped
// across a newline is still captured whole. Global for iteration.
const ANCHOR_RE = /\[\[JD_ANCHOR:\s*([\s\S]*?)\]\]/g;

/**
 * Split `source` into ordered markdown / anchor segments.
 *
 * Adjacent/leading/trailing whitespace-only markdown segments are dropped so
 * the renderer doesn't emit empty `<p>` blocks around anchors.
 */
export function splitAnchorSegments(source: string): EvalSegment[] {
  if (typeof source !== "string" || source.length === 0) return [];
  if (!source.includes("[[JD_ANCHOR")) {
    return [{ kind: "markdown", text: source }];
  }

  const segments: EvalSegment[] = [];
  let lastIndex = 0;
  // Reset lastIndex — module-level regex is stateful across calls.
  ANCHOR_RE.lastIndex = 0;

  let match: RegExpExecArray | null;
  while ((match = ANCHOR_RE.exec(source)) !== null) {
    const before = source.slice(lastIndex, match.index);
    if (before.trim().length > 0) {
      segments.push({ kind: "markdown", text: before });
    }
    const quote = (match[1] ?? "").trim();
    if (quote.length > 0) {
      segments.push({ kind: "anchor", quote });
    }
    lastIndex = match.index + match[0].length;
  }

  const tail = source.slice(lastIndex);
  if (tail.trim().length > 0) {
    segments.push({ kind: "markdown", text: tail });
  }

  return segments;
}

/** True iff `source` contains at least one JD anchor marker. */
export function hasAnchors(source: string): boolean {
  return typeof source === "string" && source.includes("[[JD_ANCHOR");
}
